import { Injectable } from '@angular/core';
import { Platform } from '@ionic/angular';
import { environment } from 'src/environments/environment';
import { LoginResponse } from 'src/app/models/user/login-response';
import { User } from 'src/app/models/user/user';
import { UserPermissions } from 'src/app/models/user/user-permissions';
import { StorageService } from 'src/app/services/storage/storage.service';

@Injectable({
  providedIn: 'root'
})
export class UserDataService {

  APP_VERSION: string = 'app_version';
  COMPILATION_DATE: string = 'compilation_date';
  GATEWAY_URL: string = 'gateway_url';
  SYSTEM_INFORMATION: string = 'system_information';
  USER_NAME: string = 'username';
  USER_EMAIL: string = 'email';
  API_KEY: string = 'api_key';
  AUTOCOMPLETE_TIME: string = 'autocompleteTime';
  USER: string = 'user';
  USER_PERMISSIONS: string = 'permissionObject';
  GATEWAY_VERSION: string = 'gatewayVersion';
  URL_TO_REDIRECT: string = "urlToRedirect"

  constructor(private storageService: StorageService,
    private platform: Platform) { }

  async isGatewayConfigured(): Promise<boolean> {
    var url = await this.storageService.getEntry(this.GATEWAY_URL);
    return url != null;
  }

  getGatewayUrl(): Promise<any> {
    return this.storageService.getEntry(this.GATEWAY_URL);
  }

  setGatewayUrl(url: string): Promise<any> {
    return this.storageService.setEntry(this.GATEWAY_URL, url);
  }

  logoutOfGateway(): Promise<any> {
    return this.storageService.removeEntry(this.GATEWAY_URL);
  }

  async hasLoggedIn(): Promise<boolean> {
    var api_key = await this.storageService.getEntry(this.API_KEY);
    return api_key != null;
  }

  async setLoginData(response: LoginResponse): Promise<void> {
    await this.storageService.setEntry(this.USER_NAME, response.user.userName);
    await this.storageService.setEntry(this.USER_EMAIL, response.user.emailAddress);
    await this.storageService.setEntry(this.API_KEY, response.api_key);
    var autocompleteTime = environment.DEFAULT_AUTOCOMPLETE_DEBOUNCE_TIME;
    if (response.autocompleteTime && response.autocompleteTime > environment.DEFAULT_AUTOCOMPLETE_DEBOUNCE_TIME) {
      autocompleteTime = response.autocompleteTime;
    }
    await this.storageService.setEntry(this.AUTOCOMPLETE_TIME, autocompleteTime);
    await this.storageService.setEntry(this.USER, response.user);
    await this.storageService.setEntry(this.USER_PERMISSIONS, response.permissions);
    await this.storageService.setEntry(this.GATEWAY_VERSION, response.gatewayVersion);
  }

  async removeLoginData(): Promise<void> {
    await this.storageService.removeEntry(this.USER_NAME);
    await this.storageService.removeEntry(this.USER_EMAIL);
    await this.storageService.removeEntry(this.API_KEY);
    await this.storageService.removeEntry(this.AUTOCOMPLETE_TIME);
    await this.storageService.removeEntry(this.USER);
    await this.storageService.removeEntry(this.USER_PERMISSIONS);
    await this.storageService.removeEntry(this.GATEWAY_VERSION);
  }

  getApiKey(): Promise<any> {
    return this.storageService.getEntry(this.API_KEY);
  }

  getPlatform() {
    var platformType = 'web';
    if (this.platform.is('android')) {
      platformType = "android";
    } else if (this.platform.is('ios')) {
      platformType = 'ios';
    }
    return platformType;
  }

  isMobilePlatform() {
    return this.getPlatform() != 'web';
  }

  getUser(): Promise<User>{
    return this.storageService.getEntry(this.USER);
  }

  getUserPermissions(): Promise<UserPermissions> {
    return this.storageService.getEntry(this.USER_PERMISSIONS);
  }

  async isReadOnly(): Promise<boolean>{
    return (await this.getUserPermissions()).readOnly;
  }

  async isSetupReadOnly(): Promise<boolean>
  {
    return (await this.getUserPermissions()).setupReadOnly;
  }

  getAutocompleteTime(): Promise<number> {
    return this.storageService.getEntry(this.AUTOCOMPLETE_TIME);
  }

  getGatewayVersion(): Promise<string> {
    return this.storageService.getEntry(this.GATEWAY_VERSION);
  }
  
  getUrlToRedirect() {
    return this.storageService.getEntry(this.URL_TO_REDIRECT);
  }

  setUrlToRedirect(url: string): Promise<any> {
    return this.storageService.setEntry(this.URL_TO_REDIRECT, url);
  }

  resetUrlToRedirect(): Promise<boolean> {
    return this.storageService.removeEntry(this.URL_TO_REDIRECT);
  }
}
